const DAY_MS = 24 * 60 * 60 * 1000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

function timestamp(value) {
  if (value === undefined || value === null || value === '') return null;
  const milliseconds = new Date(value).getTime();
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

function resultWindow(tournament) {
  // The API uses resultAt (currently equal to endAt), not updatedAt or a notice date.
  const resultAt = timestamp(tournament?.resultAt) ?? timestamp(tournament?.endAt);
  if (resultAt === null) return null;
  const nextKstMidnight = (Math.floor((resultAt + KST_OFFSET_MS) / DAY_MS) + 1) * DAY_MS - KST_OFFSET_MS;
  return { start: resultAt, end: nextKstMidnight };
}

export function getTournamentSignupCountdown(deadline, now = Date.now()) {
  const end = timestamp(deadline);
  const current = timestamp(now);
  if (end === null || current === null || current >= end) return { label: '', nextChangeAt: null };
  const today = Math.floor((current + KST_OFFSET_MS) / DAY_MS);
  const days = Math.floor((end + KST_OFFSET_MS) / DAY_MS) - today;
  return {
    label: days > 0 ? `D-${days}` : 'D-Day',
    nextChangeAt: Math.min(end, (today + 1) * DAY_MS - KST_OFFSET_MS),
  };
}

/**
 * Pure home-entry placement policy. Signup opens inclusively; the result day
 * ends exclusively at the following KST midnight. The server rolls the current
 * tournament forward at resultAt, leaving that day's results in lastTournament.
 */
export function getTournamentEntryPlacement(summary, now = Date.now()) {
  const nowMs = timestamp(now);
  if (nowMs === null) return { isFeatured: false, nextChangeAt: null };

  const windows = [];
  const current = summary?.currentTournament;
  const currentResult = resultWindow(current);
  if (['scheduled', 'signup', 'active'].includes(current?.status)) {
    const signupAt = timestamp(current.signupOpensAt);
    if (signupAt !== null && currentResult && signupAt < currentResult.end) {
      windows.push({ start: signupAt, end: currentResult.end });
    } else if (['signup', 'active'].includes(current.status)) {
      // Older/incomplete responses can still state a live phase. Any supplied
      // dates remain hard bounds; completed entries require a real result date.
      windows.push({ start: signupAt ?? -Infinity, end: currentResult?.end ?? Infinity });
    }
  }

  [current, summary?.lastTournament].forEach((tournament) => {
    if (tournament?.status !== 'completed') return;
    const result = resultWindow(tournament);
    if (result) windows.push(result);
  });

  const futureBoundaries = windows.flatMap(({ start, end }) => [start, end])
    .filter((boundary) => Number.isFinite(boundary) && boundary > nowMs);
  return {
    isFeatured: windows.some(({ start, end }) => nowMs >= start && nowMs < end),
    nextChangeAt: futureBoundaries.length ? Math.min(...futureBoundaries) : null,
  };
}
